import { createFileRoute, Link } from "@tanstack/react-router";
import { useGetDefectById } from "@/api/client-defect";
import { useGetEquipmentById } from "@/api/client-equipment";
import { ApiError } from "@/api/custom-fetch";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { requireMinRole } from "@/lib/route-guards";
import { DefectDetailsPage } from "@/sections/defects/details";

export const Route = createFileRoute("/dashboard/defects/$defectId")({
  component: Component,
  beforeLoad: ({ context }) => {
    // Viewer floor — read-only defect detail for viewers; mutating controls
    // are gated within the page. See the list route for rationale.
    requireMinRole(context.session, "viewer");
  },
  staticData: {
    title: "Defect Details",
    parent: { title: "Defects", path: "/dashboard/defects" },
  },
});

function DefectSkeleton() {
  return (
    <div className="p-4 lg:p-7 pb-24">
      {/* Header skeleton */}
      <div className="mb-6">
        <Skeleton className="h-8 w-32 mb-4" />
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-5 w-96" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      </div>

      {/* Cards skeleton */}
      <div className="grid gap-6 md:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Component() {
  const { defectId } = Route.useParams();

  const {
    data: defect,
    isLoading: isDefectLoading,
    error: defectError,
    refetch: refetchDefect,
  } = useGetDefectById(defectId);

  // Fire equipment query as soon as we know the equipmentId
  const { data: equipment, isLoading: isEquipmentLoading } =
    useGetEquipmentById(defect?.equipmentId ?? "", {
      query: { enabled: !!defect?.equipmentId },
    });

  if (isDefectLoading) {
    return <DefectSkeleton />;
  }

  // A 404 means the defect doesn't exist (bad id / removed record) — show a
  // not-found state with a way back, mirroring the inspections detail route.
  // Any other failure (or no error but missing data) is a genuine load error
  // and keeps the retry affordance.
  if (defectError instanceof ApiError && defectError.status === 404) {
    return (
      <PageStatus
        variant="not-found"
        title="Defect not found"
        description="This defect may have been removed, or the reference in the link is incorrect. Check the reference and try again from the defects list."
        actions={
          <>
            <Button
              nativeButton={false}
              render={<Link to="/dashboard/defects" />}
            >
              Back to defects
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to="/dashboard" />}
            >
              Go to dashboard
            </Button>
          </>
        }
      />
    );
  }

  if (defectError || !defect) {
    return (
      <PageStatus
        variant="error"
        title="Failed to load defect"
        description="We couldn't load this defect record. It may have been removed, or there was a problem reaching the server. Try again, or return to the defects list."
        actions={
          <>
            <Button onClick={() => refetchDefect()}>Try again</Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to="/dashboard/defects" />}
            >
              Back to defects
            </Button>
          </>
        }
      />
    );
  }

  return (
    <DefectDetailsPage
      defect={defect}
      equipment={equipment}
      isEquipmentLoading={isEquipmentLoading}
    />
  );
}
